import React, { useEffect } from 'react';

import { useSharedPluginState } from '@atlaskit/editor-common/hooks';
import type { PMPlugin, SelectionToolbarGroup } from '@atlaskit/editor-common/types';
import { useSharedPluginStateSelector } from '@atlaskit/editor-common/use-shared-plugin-state-selector';
import { fg } from '@atlaskit/platform-feature-flags';

import type { AIDefinitionsPlugin } from './aiDefinitionsPluginType';
import { onLivePageModeChange } from './pm-plugins/ai-definitions/commands';
import { createAIDefinitionsPMPlugin } from './pm-plugins/ai-definitions/plugin';
import { aiDefinitionsPluginKey } from './pm-plugins/ai-definitions/plugin-key';
import {
	setupKeywordStylerMutationObserver,
	updateAllKeywordStylerPositions,
} from './pm-plugins/ai-definitions/util/keyword-styler';
import { getAIDefineButton } from './pm-plugins/selection-toolbar-buttons/define';
import { AIDefinitions } from './ui/AIDefinitions';
import { AIDefinitionsErrorBoundary } from './ui/AIDefinitionsErrorBoundary';
import { isLivePageViewMode } from './ui/utils/api';
import { selectionHasText } from './ui/utils/selection';

export const aiDefinitionsPlugin: AIDefinitionsPlugin = ({ api, config }) => {
	return {
		name: 'aiDefinitions',

		usePluginHook: fg('platform_editor_ai_definitions_pluginstateeffect')
			? ({ editorView }) => {
					const livePageMode = useSharedPluginStateSelector(api, 'editorViewMode.mode');

					useEffect(() => {
						onLivePageModeChange({ api, config, editorView, livePageMode });
						if (livePageMode === 'view') {
							// when the decorations aren't changed,
							// the decoration DOM nodes are already inserted when `view` is called on Hello (but not locally)
							// so there must be time racing here, but not sure if all nodes are ready
							// so always update DOM nodes positions to make sure they are positioned correctly
							updateAllKeywordStylerPositions();

							return setupKeywordStylerMutationObserver(editorView.dom);
						}
					}, [editorView, livePageMode]);
				}
			: ({ editorView }) => {
					const { editorViewModeState } = useSharedPluginState(api, ['editorViewMode']);
					const livePageMode = editorViewModeState?.mode;

					useEffect(() => {
						onLivePageModeChange({ api, config, editorView, livePageMode });
					}, [editorView, livePageMode]);
				},

		pmPlugins() {
			const pmPlugins: PMPlugin[] = [];

			pmPlugins.push({
				name: 'aiDefinitions',
				plugin: (params) => createAIDefinitionsPMPlugin(params, { api, config }),
			});

			return pmPlugins;
		},

		getSharedState(editorState) {
			if (!editorState) {
				return null;
			}
			const pluginState = aiDefinitionsPluginKey.getState(editorState);

			if (!pluginState) {
				return null;
			}

			return {
				isReadingAidsPopupOpen: pluginState.isReadingAidsPopupOpen,
				readingAidsPopupKey: pluginState.readingAidsPopupKey,
				readingAidsPopupProps: pluginState.readingAidsPopupProps,
			};
		},

		contentComponent: ({ editorView }) => {
			if (fg('platform_editor_ai_definitions_pluginstateeffect')) {
				return (
					<AIDefinitionsErrorBoundary api={api}>
						<AIDefinitions
							api={api}
							editorView={editorView}
							contentId={config.contentId}
							ReadingAidsPopup={config.ReadingAidsPopup}
						/>
					</AIDefinitionsErrorBoundary>
				);
			} else {
				return (
					<AIDefinitions
						api={api}
						editorView={editorView}
						contentId={config.contentId}
						ReadingAidsPopup={config.ReadingAidsPopup}
					/>
				);
			}
		},

		pluginsOptions: {
			selectionToolbar: (state, intl) => {
				const items: SelectionToolbarGroup['items'] = [];

				if (fg('platform_editor_ai_definitions_pluginstateeffect')) {
					// only show AI define button in consumption mode
					if (
						api &&
						api?.editorViewMode.sharedState.currentState()?.mode === 'view' &&
						selectionHasText(state)
					) {
						items.push(getAIDefineButton({ intl, state, config, api }));
					}
				} else {
					if (api && isLivePageViewMode({ api }) && selectionHasText(state)) {
						items.push(getAIDefineButton({ intl, state, config, api }));
					}
				}

				if (items.length) {
					return {
						rank: -10,
						isToolbarAbove: true,
						items,
					};
				}
			},
		},
	};
};
